import { useEffect, useState, type FormEvent } from "react";
import { copyConnection, errorMessage } from "../api";
import {
  scopeOf,
  type CatalogProvider,
  type Connection,
  type Project,
  type Team,
} from "../types";
import type { ToastState } from "./Toast";

interface Props {
  provider: CatalogProvider;
  conn: Connection;
  /** The user's real teams -- the personal team is filtered out by the caller. */
  teams: Team[];
  /** Every project the user can see; grouped under its team here. */
  projects: Project[];
  onClose: () => void;
  notify: (message: string, kind?: ToastState["kind"]) => void;
}

/**
 * Share a copy of one connected account with a team: pick which team, and which of its
 * projects the copy should appear in. Same dialog shape as moving a project -- backdrop
 * and Escape close it, and it stays open on a refusal, because the error explains what
 * to change (already shared there, reconnect first).
 *
 * One select, grouped by team, because the decision is really "where does this land" --
 * a project -- and the team is how you find it, not a second question.
 */
export default function CopyToTeamDialog({
  provider,
  conn,
  teams,
  projects,
  onClose,
  notify,
}: Props) {
  /**
   * A team connection's own project is left out: copying it to where it already is can
   * only ever answer with an error, so the picker does not offer it -- same reasoning
   * as the move dialog leaving out the default project.
   */
  const groups = teams
    .map((team) => ({
      team,
      projects: projects.filter(
        (p) =>
          p.teamId === team.id &&
          !(scopeOf(conn) === "team" && p.id === conn.projectId)
      ),
    }))
    .filter((g) => g.projects.length > 0);

  const [projectId, setProjectId] = useState(groups[0]?.projects[0]?.id ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!projectId || busy) return;

    setBusy(true);
    try {
      const res = await copyConnection({ connectionId: conn.id, projectId });
      notify(
        `${conn.accountLabel} shared with ${res.data.teamName} — its tools appear ` +
          `in "${res.data.projectName}" as ${res.data.slug}__*.`
      );
      onClose();
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form
        className="modal modal-narrow"
        role="dialog"
        aria-modal="true"
        aria-label="Copy to a team"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => void submit(e)}
      >
        <header className="modal-head">
          <div>
            <h2>Copy to a team</h2>
            <p className="modal-sub">
              {conn.accountLabel} · {provider.name}
            </p>
          </div>
          <button
            className="modal-close"
            type="button"
            disabled={busy}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="modal-body">
          {groups.length === 0 ? (
            <p className="wizard-note">
              There is nowhere left to copy this account to -- it is already shared in
              every team project you are in.
            </p>
          ) : (
            <>
              <div className="field">
                <label>Share in</label>
                <select
                  value={projectId}
                  disabled={busy}
                  onChange={(e) => setProjectId(e.target.value)}
                >
                  {groups.map(({ team, projects: teamProjects }) => (
                    <optgroup key={team.id} label={team.name}>
                      {teamProjects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <p className="wizard-note">
                Everyone in that team gets this account's tools, through your
                credential. The copy is independent — its own name, switch and tool
                selection — and starts switched off for every member. Disconnecting
                one copy never touches the other.
              </p>
            </>
          )}
        </div>

        <footer className="modal-foot">
          <button
            className="btn btn-subtle"
            type="button"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </button>
          {groups.length > 0 && (
            <button className="btn" type="submit" disabled={busy || !projectId}>
              {busy ? "Copying…" : "Copy to team"}
            </button>
          )}
        </footer>
      </form>
    </div>
  );
}
